import { useCallback, useEffect, useRef, useState } from 'react';
import { I } from '../components/icons.jsx';
import { KineDBError } from '@kinedb/client';
import { wsSql, wsTransaction, getSocket, subscribeStatus } from '../data/socket.js';
import { getCurrentDb, subscribeCurrentDb } from '../data/db-store.js';
import { getCurrentBranch } from '../data/branch-store.js';
import { createSchemaLoader } from '../data/schema-loader.js';
import { createColTypeCache } from '../data/col-type-cache.js';
import { parseWatchable } from '../data/sql-watch.js';
import { flattenDocumentColumn } from '../data/flatten-documents.js';
import * as live from '../data/live-store.js';
import { splitSql } from '../data/split-sql.js';
import { planBlocks } from '../data/txn-blocks.js';
import { formatTypedCell, jsonSafeReplacer } from '../data/cell-format.js';

function sqlHighlight(src) {
  const KW  = /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|LIMIT|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AS|AND|OR|NOT|IN|IS|NULL|TRUE|FALSE|DESC|ASC|WITH|EXPLAIN|ANALYZE|INSERT|UPDATE|DELETE|CREATE|TABLE|INDEX|HAVING|DISTINCT|UNION|CASE|WHEN|THEN|ELSE|END|SHOW|TABLES|COLUMNS|VALUES|INTO|SET|PRIMARY KEY|DROP|BEGIN|COMMIT|ROLLBACK|WATCH|UNWATCH)\b/gi;
  const FN  = /\b(COUNT|SUM|AVG|MIN|MAX|COALESCE|NOW|DATE_TRUNC|EXTRACT|TO_CHAR)\b/gi;
  const STR = /'[^']*'/g;
  const NUM = /\b\d+(\.\d+)?\b/g;
  const COM = /--[^\n]*/g;
  let out = src
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(COM, m => `com${m}`)
    .replace(STR, m => `str${m}`)
    .replace(KW,  m => `kw${m}`)
    .replace(FN,  m => `fn${m}`)
    .replace(NUM, m => `num${m}`);
  out = out.replace(/(\w+)([\s\S]*?)/g, (_, t, v) => `<span class="tok-${t}">${v}</span>`);
  return out;
}

// localStorage-backed state. Skips the read on SSR and silently no-ops if
// storage is unavailable (private mode, quota exceeded).
function useLocalState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? initial : JSON.parse(raw);
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
  }, [key, value]);
  return [value, setValue];
}

const DEFAULT_TABS = [
  { id: 1, name: 'Tables',     query: 'SHOW TABLES' },
  { id: 2, name: 'Untitled 2', query: '' },
];

// kinedb-query doesn't accept a trailing `;` (it's not part of the grammar).
// Strip the universal SQL habit so paste-from-elsewhere queries just work.
function normalizeSql(sql) {
  return sql.replace(/;\s*$/, '').trim();
}

// Prepend EXPLAIN to the user's SQL (no-op if it's already there). Run when
// the Plan view tab is active so the user gets a plan back instead of rows.
function withExplain(sql) {
  return /^\s*EXPLAIN\b/i.test(sql) ? sql : `EXPLAIN ${sql}`;
}

// Streaming-watch: capacity for the per-tab ring buffer of *lines* shown.
// Each row in a multi-row watch_data frame becomes its own line, and a
// single seeding burst can carry thousands of rows — cap so the buffer
// never balloons.
const WATCH_RING_CAPACITY = 1000;

// Parse `WATCH foo [VERBOSE] [FAST] [WHERE expr]` from the editor. Returns
// null on UNWATCH, and `{ table, opts }` on WATCH. Lets the editor act as
// a real change-stream tail (separate from Live mode, which re-runs a
// SELECT and diffs results).
function parseEditorWatch(sql) {
  const w = sql.match(/^\s*WATCH\s+([A-Za-z_][A-Za-z0-9_]*)\s*(.*?)\s*$/i);
  if (!w) return null;
  const table = w[1];
  const tail = w[2] || '';
  const verbose = /\bVERBOSE\b/i.test(tail);
  const fast    = /\bFAST\b/i.test(tail);
  const m = tail.match(/\bWHERE\s+(.*)$/i);
  const where = m ? m[1].trim() : undefined;
  return { table, opts: { verbose, fast, ...(where ? { where } : {}) } };
}
function parseEditorUnwatch(sql) {
  const m = sql.match(/^\s*UNWATCH\s+([A-Za-z_][A-Za-z0-9_]*)\s*$/i);
  return m ? m[1] : null;
}

// Cheap structural equality on two query results. Used by Live mode to
// decide whether a re-run actually changed this tab's view (so we can
// suppress the unseen tick for irrelevant UPDATEs / DELETEs that the server
// can't filter out today). Ignores `elapsed_us` — that varies every run.
function resultsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.type !== b.type) return false;
  if (a.type !== 'rows') {
    return a.count === b.count && (a.message || '') === (b.message || '');
  }
  if (a.count !== b.count) return false;
  if (JSON.stringify(a.columns) !== JSON.stringify(b.columns)) return false;
  // `jsonSafeReplacer` because B607 cells can be a BigInt, which plain
  // `JSON.stringify` THROWS on — that would break the Live diff loop outright.
  return JSON.stringify(a.data, jsonSafeReplacer) === JSON.stringify(b.data, jsonSafeReplacer);
}

// kinedb-query EXPLAIN returns rows with exactly these three columns. We use
// this shape to detect plan results regardless of how they got there (typed
// EXPLAIN in the editor or auto-prepended via the Plan tab).
function isPlanResult(result) {
  return result?.type === 'rows'
    && Array.isArray(result.columns)
    && result.columns.length === 3
    && result.columns[0] === 'step'
    && result.columns[1] === 'operation'
    && result.columns[2] === 'detail';
}

// Color hint per operation kind. Cheap operations green, expensive red.
const OP_COLOR = {
  'point lookup':         'var(--green)',
  'pk lookup':            'var(--green)',
  'index scan (prefix)':  'var(--blue)',
  'index scan (range)':   'var(--blue)',
  'filter':               'var(--orange)',
  'full scan':            '#ff5e5e',
};

// Sub-millisecond server times deserve real precision; keep a single tier of
// readability past 10ms.
function formatServerUs(us) {
  if (us == null) return null;
  if (us < 1000)    return `${us}µs`;
  if (us < 10_000)  return `${(us / 1000).toFixed(1)}ms`;
  return `${Math.round(us / 1000)}ms`;
}

// Compact "Xms client · Yµs server" pair shown in the results header. Number
// includes the unit so the gray label is just the source ("client"/"server"),
// which keeps the second word legible. Per-`<b>` inline color is required
// because .qc-results-head .stats .s b in app.css hardcodes white.
function ElapsedStats({ clientMs, serverUs }) {
  return (
    <>
      <span className="s" title="Round-trip measured by the browser (includes network + JSON parse)" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <I.Refresh size={11} style={{ color: 'var(--app-fg3)' }} />
        <b style={{ color: 'var(--app-fg1)' }}>{clientMs ?? 0}ms</b>
        <span style={{ color: 'var(--app-fg2)' }}>client</span>
      </span>
      {serverUs != null && (
        <span className="s" title="Server-side execution time reported by kinedb-server (elapsed_us)" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <I.Database size={11} style={{ color: 'var(--cyan)' }} />
          <b style={{ color: 'var(--cyan)' }}>{formatServerUs(serverUs)}</b>
          <span style={{ color: 'var(--app-fg2)' }}>server</span>
        </span>
      )}
    </>
  );
}

// ── Schema tree ───────────────────────────────────────────────────────
//
// `SHOW COLLECTIONS` returns every registered collection as `[name, type]`
// where type is one of TABLE | LEDGER | KV | DOCUMENT. All four kinds
// render in a single unified list with per-kind icons; relational kinds
// (table/ledger) expand to columns via `SHOW COLUMNS FROM x`.

// Starter query when a tree node is clicked. Keyed by lowercase server label.
const STARTER_QUERY = {
  table:    name => `SELECT * FROM ${name} LIMIT 100`,
  ledger:   name => `SELECT * FROM ${name} LIMIT 100`,
  kv:       name => `GET '<key>' FROM ${name}`,
  document: name => `SELECT * FROM ${name} LIMIT 100`,
};

const TYPE_COLOR = {
  table:    'var(--cyan)',
  ledger:   'var(--cyan)',
  kv:       'var(--purple)',
  document: 'var(--blue)',
};

const TYPE_ICON = {
  table:    'Table',
  ledger:   'Database',
  kv:       'Key',
  document: 'Audit',
};

const isRelational = (t) => t === 'table' || t === 'ledger';

function SchemaTree({ filter, onOpenAsTab, onWatchTable }) {
  const [entries, setEntries] = useState(null); // null = loading, [{name,type}] = loaded
  const [error, setError]     = useState(null);
  const [open, setOpen]       = useState({ schema: true });
  const [columns, setColumns] = useState({});   // table name → [{col,type,pk}] | {error}

  // Track current error/columns shape in a ref so the status-change listener
  // (which captures the closure once at mount) can read live values without
  // forcing the listener to re-bind on every state change.
  const stateRef = useRef({ error: null, columns: {} });
  useEffect(() => { stateRef.current.error = error; stateRef.current.columns = columns; }, [error, columns]);

  // B1691: the loader sequences overlapping reloads (mount, reconnect, Retry, a
  // database switch). A reload during a load runs once more after it, and the
  // answer of a superseded load never reaches the tree.
  const loaderRef = useRef(null);
  if (!loaderRef.current) {
    loaderRef.current = createSchemaLoader({
      fetch: async () => {
        const r = await wsSql('SHOW COLLECTIONS');
        // Old servers (pre-d0b5329) returned single-column [name]; new ones return [name, type].
        return r.data.map(row => ({ name: row[0], type: row.length > 1 ? String(row[1]).toLowerCase() : 'table' }));
      },
      onStart:  () => { setError(null); setEntries(null); },
      onResult: (list) => setEntries(list),
      onError:  (e) => { setError(e.message || String(e)); setEntries([]); },
    });
  }
  const reload = useCallback(() => loaderRef.current.reload(), []);

  useEffect(() => { reload(); }, [reload]);

  // Auto-recover after a node restart: when the socket reconnects, retry
  // SHOW COLLECTIONS if we previously failed, and refresh any per-table
  // column lookups that errored out during the offline window. Without
  // this the user sees a stale "connection closed" error indefinitely
  // even though every other part of the UI has reattached.
  useEffect(() => subscribeStatus((status) => {
    if (status !== 'connected') return;
    if (stateRef.current.error) {
      reload();
    }
    // Drop column-cache entries that errored so they re-fetch on the next
    // expand. Successfully-fetched entries are kept — the schema rarely
    // changes during a restart and re-fetching every column list would
    // be unnecessary load.
    const cols = stateRef.current.columns;
    const stale = Object.keys(cols).filter(k => cols[k] && cols[k].error);
    if (stale.length) setColumns(c => {
      const next = { ...c };
      for (const k of stale) delete next[k];
      return next;
    });
  }), [reload]);

  // B76: when the session switches database (USE via the picker), the table
  // list belongs to a different namespace — reload it and drop the per-table
  // column caches so the tree reflects the new current database.
  useEffect(() => subscribeCurrentDb(() => {
    setColumns({});
    reload();
  }), [reload]);

  const toggleTable = async (name) => {
    setOpen(o => ({ ...o, [name]: !o[name] }));
    if (!columns[name]) {
      try {
        const r = await wsSql(`SHOW COLUMNS FROM ${name}`);
        setColumns(c => ({ ...c, [name]: r.data.map(([col, type, pk]) => ({ col, type, pk: pk === 'YES' })) }));
      } catch (e) {
        setColumns(c => ({ ...c, [name]: { error: e.message || String(e) } }));
      }
    }
  };

  if (error) return (
    <div className="qc-tree" style={{ padding: 16, color: 'var(--orange)', fontSize: 12 }}>
      <I.AlertTriangle size={12} /> {error}
      <button className="btn btn-sm btn-ghost" onClick={reload} style={{ display: 'block', marginTop: 8 }}><I.Refresh size={11} /> Retry</button>
    </div>
  );
  if (entries === null) return <div className="qc-tree" style={{ padding: 16, color: 'var(--app-fg3)', fontSize: 12 }}>Loading…</div>;

  const matches = (name) => !filter || name.toLowerCase().includes(filter.toLowerCase());
  const filtered = entries.filter(e => matches(e.name));

  return (
    <div className="qc-tree">
      <div className="tree-node" onClick={() => setOpen(o => ({ ...o, schema: !o.schema }))} style={{ fontWeight: 600, color: 'var(--app-fg1)' }}>
        {open.schema ? <I.ChevDown size={11} className="chev" /> : <I.ChevRight size={11} className="chev" />}
        <I.Database size={13} style={{ color: 'var(--cyan)' }} />
        <span>Schema</span>
        <span className="meta">{entries.length}</span>
      </div>
      {open.schema && (
        <div className="tree-children">
          {entries.length === 0 && (
            <div style={{ padding: '4px 12px', fontSize: 11, color: 'var(--app-fg3)', lineHeight: 1.5 }}>
              Empty. Run <span className="mono">CREATE TABLE …</span> or <span className="mono">CREATE COLLECTION x TYPE KV</span>.
            </div>
          )}
          {filtered.length === 0 && entries.length > 0 && (
            <div style={{ padding: '4px 12px', fontSize: 11, color: 'var(--app-fg3)' }}>No matches.</div>
          )}
          {filtered.map(e => {
            const Icon = I[TYPE_ICON[e.type]] ?? I.Table;
            const expand = isRelational(e.type);
            return (
              <div key={e.name}>
                <div className="tree-node is-table">
                  <span
                    onClick={() => expand ? toggleTable(e.name) : onOpenAsTab(e.name, STARTER_QUERY[e.type](e.name))}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                    {expand
                      ? (open[e.name] ? <I.ChevDown size={11} className="chev" /> : <I.ChevRight size={11} className="chev" />)
                      : <span className="chev" />}
                    <Icon size={13} style={{ color: TYPE_COLOR[e.type] }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</span>
                  </span>
                  {e.type !== 'table' && (
                    <span className="meta" style={{ color: TYPE_COLOR[e.type], textTransform: 'uppercase' }}>{e.type}</span>
                  )}
                  <button
                    className="btn btn-icon btn-ghost"
                    onClick={ev => { ev.stopPropagation(); onOpenAsTab(e.name, STARTER_QUERY[e.type](e.name)); }}
                    style={{ marginLeft: 4, padding: 2, width: 18, height: 18 }}
                    title="Open starter query in a new tab">
                    <I.Play size={9} />
                  </button>
                  <button
                    className="btn btn-icon btn-ghost"
                    onClick={ev => { ev.stopPropagation(); onWatchTable(e.name); }}
                    style={{ marginLeft: 2, padding: 2, width: 18, height: 18, color: 'var(--cyan)' }}
                    title={`Watch ${e.name} VERBOSE in a new tab`}>
                    <I.Eye size={9} />
                  </button>
                  <button
                    className="btn btn-icon btn-ghost"
                    onClick={ev => { ev.stopPropagation(); window.location.hash = '#/access/collection/' + encodeURIComponent(e.name); }}
                    style={{ marginLeft: 2, padding: 2, width: 18, height: 18 }}
                    title={`Who has access to ${e.name}`}>
                    <I.Lock size={9} />
                  </button>
                </div>
                {expand && open[e.name] && columns[e.name] && (
                  <div className="tree-children">
                    {columns[e.name].error
                      ? <div style={{ padding: '4px 12px', fontSize: 11, color: 'var(--orange)' }}>{columns[e.name].error}</div>
                      : columns[e.name].map(c => (
                          <div key={c.col} className="tree-node is-col">
                            <span className="chev" />
                            {c.pk ? <I.Key size={11} style={{ color: 'var(--orange)' }} /> : <I.Column size={11} />}
                            <span>{c.col}</span>
                            <span className="meta">{c.type}</span>
                          </div>
                        ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── History + Saved (localStorage) ────────────────────────────────────

function HistoryList({ items, filter, onPick }) {
  const filtered = filter ? items.filter(h => h.sql.toLowerCase().includes(filter.toLowerCase())) : items;
  if (items.length === 0) {
    return <div className="qc-tree" style={{ padding: 16, fontSize: 12, color: 'var(--app-fg3)' }}>Run a query to start your history.</div>;
  }
  return (
    <div className="qc-tree" style={{ padding: '4px 8px' }}>
      {filtered.map((h, i) => (
        <div key={i}
          onClick={() => onPick(h)}
          style={{ padding: '8px', borderRadius: 6, marginBottom: 4, cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--app-hover)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span className={'kdot' + (h.status === 'err' ? ' orange' : '')} />
            <span style={{ fontSize: 11, color: 'var(--app-fg3)' }}>{relTime(h.ts)}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--app-fg4)', fontFamily: 'var(--app-mono)' }}>
              {h.ms}ms{h.serverUs != null ? ` / ${formatServerUs(h.serverUs)}` : ''}{typeof h.count === 'number' ? ` · ${h.count}r` : ''}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--app-fg2)', fontFamily: 'var(--app-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.sql}</div>
        </div>
      ))}
    </div>
  );
}

function SavedList({ items, filter, onPick, onDelete }) {
  const filtered = filter ? items.filter(s => s.name.toLowerCase().includes(filter.toLowerCase())) : items;
  if (items.length === 0) {
    return <div className="qc-tree" style={{ padding: 16, fontSize: 12, color: 'var(--app-fg3)' }}>Save the current query (toolbar) to add it here.</div>;
  }
  return (
    <div className="qc-tree">
      <div className="tree-children">
        {filtered.map((s, i) => (
          <div key={i} className="tree-node" onClick={() => onPick(s)} style={{ position: 'relative' }}>
            <span className="chev" />
            <I.Save size={11} style={{ color: 'var(--app-fg4)' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
            <button
              onClick={e => { e.stopPropagation(); onDelete(i); }}
              className="btn btn-icon btn-ghost"
              style={{ marginLeft: 'auto', padding: 2, width: 18, height: 18 }}
              title="Remove">
              <I.Close size={10} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function relTime(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5)    return 'just now';
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ── Editor + results ──────────────────────────────────────────────────

function QueryEditor({ value, onChange, onRun }) {
  const lines = value.split('\n');
  return (
    <div className="qc-editor">
      <div className="gutter">
        {lines.map((_, i) => <div key={i}>{i + 1}</div>)}
      </div>
      <pre dangerouslySetInnerHTML={{ __html: sqlHighlight(value) }} />
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); onRun(); }
        }}
        spellCheck={false}
        placeholder="Type a query, then press ⌘↵ to run…"
        style={{
          position: 'absolute', inset: 0, paddingLeft: 56, paddingRight: 16, paddingTop: 12, paddingBottom: 12,
          background: 'transparent', color: 'transparent', caretColor: 'var(--app-fg1)',
          font: 'inherit', border: 0, outline: 0, resize: 'none',
          fontFamily: 'var(--app-mono)', fontSize: 13, lineHeight: 1.6,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      />
    </div>
  );
}

function StatusMessage({ result }) {
  const map = {
    created:   { icon: I.CheckCircle, color: 'var(--green)',  text: 'OK' },
    inserted:  { icon: I.CheckCircle, color: 'var(--green)',  text: `${result.count} row${result.count === 1 ? '' : 's'} inserted` },
    updated:   { icon: I.CheckCircle, color: 'var(--green)',  text: `${result.count} row${result.count === 1 ? '' : 's'} updated` },
    deleted:   { icon: I.CheckCircle, color: 'var(--green)',  text: `${result.count} row${result.count === 1 ? '' : 's'} deleted` },
    watching:  { icon: I.Eye,         color: 'var(--cyan)',   text: `Watching ${result.table}` },
    unwatched: { icon: I.Eye,         color: 'var(--app-fg2)',text: `Unwatched (${result.count})` },
  };
  const m = map[result.type] || { icon: I.CheckCircle, color: 'var(--green)', text: result.type };
  const Icon = m.icon;
  return (
    <div style={{ padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: m.color }}>
      <Icon size={28} />
      <div style={{ fontSize: 14, fontWeight: 500 }}>{m.text}</div>
    </div>
  );
}

function ErrorMessage({ message }) {
  return (
    <div style={{ padding: 24, color: 'var(--orange)', fontFamily: 'var(--app-mono)', fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <I.AlertTriangle size={14} /> <b>Error</b>
      </div>
      <div style={{ color: 'var(--app-fg1)', whiteSpace: 'pre-wrap' }}>{message}</div>
    </div>
  );
}

// Slice palette + arc-path helper for the pie chart type (shared visual
// language with the reports' PieViz). Kept module-level so the render stays
// allocation-light.
const CHART_PALETTE = [
  '#00E5C7', '#0099FF', '#FFB454', '#A78BFA',
  '#F472B6', '#FBBF24', '#34D399', '#60A5FA', '#9CA3AF',
];
function pieArcPath(cx, cy, r, startAngle, endAngle) {
  // A full circle (single non-zero slice) can't be drawn as one arc whose
  // endpoints coincide — cover it with two half-arcs.
  if (endAngle - startAngle >= Math.PI * 2 - 1e-6) {
    return `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} Z`;
  }
  const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
  const x2 = cx + r * Math.cos(endAngle),   y2 = cy + r * Math.sin(endAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

// B607: the typed reply's column header as the plain type-name array the rest
// of this file already speaks (`'Timestamp'`, `'Int'`, …, or null per column).
// Returns null for an untyped 0x11 reply, so every caller keeps its old source.
function wireColTypes(result) {
  if (!Array.isArray(result?.types)) return null;
  return result.types.map(t => t?.name || null);
}

// Type names whose cells the console treats as numbers: right-aligned in the
// table, and offered as a chart's Y axis.
const NUMERIC_TYPE_NAMES = new Set(['Int', 'Float', 'Timestamp']);

// SVG bar / line / area / pie chart with X/Y column pickers and a hover
// tooltip. Auto-defaults: first non-numeric column → X (label axis), first
// numeric → Y. Both axes can be reassigned by the user via the dropdowns.
// Designed to stay small without a chart library; if you need scatter /
// log scale / multi-series, swap to recharts.
function ChartView({ result }) {
  const { columns, data, count } = result;

  // Detect numeric vs non-numeric columns. B607: when the server sent a column
  // header, ASK it — sniffing row 0 gets an all-NULL first row wrong, gets an
  // empty result wrong (no row to sniff), and cannot see that an INT past 2^53
  // is a number at all, because it arrives as a BigInt. Without the header we
  // fall back to the row-0 sniff: if a column is numeric in row 0 we trust it
  // for the whole result, since kinedb's row schemas are uniform.
  const wireTypes = wireColTypes(result);
  const isNumeric = columns.map((_, i) => wireTypes
    ? NUMERIC_TYPE_NAMES.has(wireTypes[i])
    : (count > 0 && typeof data[0][i] === 'number'));
  const firstNumeric    = isNumeric.findIndex(b => b);
  const firstNonNumeric = isNumeric.findIndex(b => !b);

  const [chartType, setChartType] = useState('bar'); // 'bar' | 'line' | 'area' | 'pie'
  const [xIdx, setXIdx] = useState(firstNonNumeric >= 0 ? firstNonNumeric : 0);
  const [yIdx, setYIdx] = useState(firstNumeric    >= 0 ? firstNumeric    : 1);
  const [hoverIdx, setHoverIdx] = useState(null);

  if (count === 0) {
    return <div style={{ padding: 32, color: 'var(--app-fg3)', textAlign: 'center', fontSize: 13 }}>0 rows.</div>;
  }
  if (firstNumeric < 0) {
    return <div style={{ padding: 32, color: 'var(--app-fg3)', textAlign: 'center', fontSize: 13 }}>Chart needs at least one numeric column.</div>;
  }

  // Series — one (label, value) per row.
  const points = data.map((row) => ({
    label: row[xIdx] == null ? '' : String(row[xIdx]),
    value: Number(row[yIdx]) || 0,
  }));
  const N = points.length;

  // ── Pie slices (only when chartType === 'pie') ──────────────
  // Drop zero/negative values (a pie of a signed series is meaningless),
  // sort desc, cap at 8 slices and roll the tail into "other".
  let pie = null;
  if (chartType === 'pie') {
    const PIE_LIMIT = 8;
    const positive = points.filter(p => p.value > 0).sort((a, b) => b.value - a.value);
    if (positive.length) {
      const top = positive.slice(0, PIE_LIMIT);
      const rest = positive.slice(PIE_LIMIT);
      const slices = rest.length
        ? [...top, { label: `other (${rest.length})`, value: rest.reduce((s, p) => s + p.value, 0) }]
        : top;
      const total = slices.reduce((s, x) => s + x.value, 0) || 1;
      const cx = 80, cy = 80, rr = 70;
      let a = -Math.PI / 2;
      const arcs = slices.map((s, i) => {
        const sweep = (s.value / total) * Math.PI * 2;
        const path = pieArcPath(cx, cy, rr, a, a + sweep);
        a += sweep;
        return { path, color: CHART_PALETTE[i % CHART_PALETTE.length], pct: s.value / total };
      });
      pie = { slices, arcs, cx, cy, rr };
    }
  }

  // viewBox dimensions. Chart scales to its container via preserveAspectRatio.
  const W = 800, H = 320, PAD_L = 56, PAD_R = 16, PAD_T = 16, PAD_B = 36;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const rawMax = Math.max(...points.map(p => p.value));
  const rawMin = Math.min(...points.map(p => p.value));
  // Always include 0 in the Y axis so bars look honest.
  const yMin = Math.min(0, rawMin);
  const yMax = Math.max(0, rawMax);
  const range = (yMax - yMin) || 1;

  const xAt = (i) => PAD_L + (N === 1 ? innerW / 2 : (i + 0.5) * (innerW / N));
  const yAt = (v) => PAD_T + innerH * (1 - (v - yMin) / range);

  // ── Render the series ──────────────────────────────────────
  let series;
  if (chartType === 'bar') {
    const barW = Math.max(2, (innerW / N) * 0.7);
    series = points.map((p, i) => {
      const yTop = yAt(Math.max(p.value, 0));
      const yBot = yAt(Math.min(p.value, 0));
      return (
        <rect key={i}
          x={xAt(i) - barW / 2} y={yTop}
          width={barW} height={Math.max(1, yBot - yTop)}
          fill="url(#kdbChartGrad)"
          opacity={hoverIdx == null || hoverIdx === i ? 1 : 0.4} />
      );
    });
  } else {
    const linePts = points.map((p, i) => `${xAt(i)},${yAt(p.value)}`).join(' ');
    series = (
      <>
        {chartType === 'area' && (
          <path d={`M ${xAt(0)},${yAt(yMin)} L ${linePts} L ${xAt(N - 1)},${yAt(yMin)} Z`}
            fill="url(#kdbChartGrad)" opacity={0.35} />
        )}
        <polyline points={linePts} fill="none" stroke="#00E5C7" strokeWidth={1.75} />
        {points.map((_, i) => (
          <circle key={i} cx={xAt(i)} cy={yAt(points[i].value)}
            r={hoverIdx === i ? 4 : 2.5} fill="#00E5C7" />
        ))}
      </>
    );
  }

  // ── Y axis ticks (3 values, with sensible formatting) ─────
  const fmt = (v) => {
    if (v === 0) return '0';
    if (Math.abs(v) >= 1000) return v.toLocaleString();
    if (Math.abs(v) < 1)     return v.toFixed(2);
    return String(Math.round(v));
  };
  const yTicks = [yMin, yMin + range / 2, yMax];

  // ── X axis labels (skip dense labels when there are too many) ─
  const xLabelStride = Math.max(1, Math.ceil(N / 12));

  // ── Hover detection ─────────────────────────────────────────
  const onMouseMove = (e) => {
    const svg = e.currentTarget;
    const ctm = svg.getScreenCTM(); if (!ctm) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    if (local.x < PAD_L || local.x > W - PAD_R) { setHoverIdx(null); return; }
    const ratio = (local.x - PAD_L) / innerW;
    const idx = Math.min(N - 1, Math.max(0, Math.floor(ratio * N)));
    setHoverIdx(idx);
  };

  // The .seg styling in app.css is scoped to .qc-toolbar / .qc-results-head,
  // so we inline equivalent styles here for the chart toolbar segmented control.
  const segWrap = {
    display: 'flex',
    background: 'var(--app-surface)',
    border: '1px solid var(--app-border)',
    borderRadius: 7,
    overflow: 'hidden',
  };
  const segBtn = (kind, label) => (
    <button key={kind}
      onClick={() => setChartType(kind)}
      style={{
        padding: '5px 12px',
        fontSize: 12,
        color: chartType === kind ? 'var(--cyan)' : 'var(--app-fg3)',
        background: chartType === kind ? 'var(--app-active)' : 'transparent',
        borderRight: kind === 'pie' ? 0 : '1px solid var(--app-border)',
      }}>
      {label}
    </button>
  );

  const selectStyle = {
    background: 'var(--app-surface)',
    border: '1px solid var(--app-border)',
    borderRadius: 6, padding: '4px 8px',
    fontSize: 12, color: 'var(--app-fg1)', cursor: 'pointer',
  };

  return (
    <div style={{ padding: '12px 16px', height: '100%', display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={segWrap}>
          {segBtn('bar', 'Bar')}
          {segBtn('line', 'Line')}
          {segBtn('area', 'Area')}
          {segBtn('pie', 'Pie')}
        </div>
        <label style={{ fontSize: 11, color: 'var(--app-fg3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>X</label>
        <select value={xIdx} onChange={e => setXIdx(Number(e.target.value))} style={selectStyle}>
          {columns.map((c, i) => <option key={i} value={i}>{c}</option>)}
        </select>
        <label style={{ fontSize: 11, color: 'var(--app-fg3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Y</label>
        <select value={yIdx} onChange={e => setYIdx(Number(e.target.value))} style={selectStyle}>
          {columns.map((c, i) => (
            <option key={i} value={i} disabled={!isNumeric[i]}>
              {c}{isNumeric[i] ? '' : ' (non-numeric)'}
            </option>
          ))}
        </select>
        {hoverIdx != null && chartType !== 'pie' && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, fontSize: 12, fontFamily: 'var(--app-mono)' }}>
            <span style={{ color: 'var(--app-fg3)' }}>{columns[xIdx]}:</span>
            <span style={{ color: 'var(--app-fg1)' }}>{points[hoverIdx].label}</span>
            <span style={{ color: 'var(--app-fg3)' }}>{columns[yIdx]}:</span>
            <span style={{ color: 'var(--cyan)' }}>{points[hoverIdx].value.toLocaleString()}</span>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {chartType === 'pie' ? (
          !pie ? (
            <div style={{ padding: 32, color: 'var(--app-fg3)', textAlign: 'center', fontSize: 13 }}>
              Pie needs at least one positive value in “{columns[yIdx]}”.
            </div>
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
              <svg viewBox="0 0 160 160" width="220" height="220" style={{ flexShrink: 0 }}>
                {pie.arcs.map((arc, i) => (
                  <path key={i} d={arc.path} fill={arc.color}
                    stroke="var(--app-bg)" strokeWidth="1"
                    opacity={hoverIdx == null || hoverIdx === i ? 1 : 0.4}
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx(null)}>
                    <title>{pie.slices[i].label}: {pie.slices[i].value.toLocaleString()} ({(arc.pct * 100).toFixed(1)}%)</title>
                  </path>
                ))}
                <circle cx={pie.cx} cy={pie.cy} r={pie.rr * 0.55} fill="var(--app-bg)" />
              </svg>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 12, fontFamily: 'var(--app-mono)', minWidth: 180 }}>
                {pie.slices.map((s, i) => (
                  <li key={i}
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx(null)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', opacity: hoverIdx == null || hoverIdx === i ? 1 : 0.5 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 2, background: pie.arcs[i].color, flexShrink: 0 }} />
                    <span style={{ color: 'var(--app-fg1)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.label}>{s.label}</span>
                    <span style={{ color: 'var(--app-fg3)' }}>{s.value.toLocaleString()}</span>
                    <span style={{ color: 'var(--cyan)', width: 48, textAlign: 'right' }}>{(pie.arcs[i].pct * 100).toFixed(1)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )
        ) : (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
          onMouseMove={onMouseMove}
          onMouseLeave={() => setHoverIdx(null)}
          style={{ width: '100%', height: '100%', display: 'block' }}>
          <defs>
            <linearGradient id="kdbChartGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor="#00FFD1" />
              <stop offset="100%" stopColor="#0099FF" />
            </linearGradient>
          </defs>
          {/* gridlines */}
          {yTicks.map((t, i) => (
            <line key={i}
              x1={PAD_L} x2={W - PAD_R}
              y1={yAt(t)} y2={yAt(t)}
              stroke="var(--app-border)" strokeWidth={1} />
          ))}
          {/* y labels */}
          {yTicks.map((t, i) => (
            <text key={i}
              x={PAD_L - 8} y={yAt(t)} dy={4}
              textAnchor="end"
              fill="var(--app-fg3)" fontSize={11} fontFamily="var(--app-mono)">
              {fmt(t)}
            </text>
          ))}
          {/* zero baseline (only if 0 is between min and max and not already a tick) */}
          {yMin < 0 && yMax > 0 && (
            <line x1={PAD_L} x2={W - PAD_R} y1={yAt(0)} y2={yAt(0)} stroke="var(--app-fg4)" strokeWidth={1} />
          )}
          {series}
          {/* x labels */}
          {points.map((p, i) => (
            (i % xLabelStride === 0 || i === N - 1) && (
              <text key={i}
                x={xAt(i)} y={H - 12}
                textAnchor="middle"
                fill="var(--app-fg3)" fontSize={11}>
                {p.label.length > 14 ? p.label.slice(0, 13) + '…' : p.label}
              </text>
            )
          ))}
          {/* hover guide */}
          {hoverIdx != null && (
            <line x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={PAD_T} y2={H - PAD_B}
              stroke="var(--cyan)" strokeOpacity={0.35} strokeWidth={1} />
          )}
        </svg>
        )}
      </div>
    </div>
  );
}

// Streaming WATCH tail. Reads from a session ref (filled by the watch
// callback at up to 10K/sec) and renders the most recent rows. The parent
// only triggers re-renders via a rAF-throttled tick, so this component
// can render at the browser's frame rate independent of event rate.
function WatchFeed({ session, onStop, onClear }) {
  // Auto-scroll is off when the user has scrolled up — preserves the
  // ability to inspect a row without it scrolling away under you. After
  // Stop, auto-scroll stops being useful (no new lines arriving), so
  // scrolling around to review is the whole point.
  const bodyRef = useRef(null);
  const stickRef = useRef(true);
  useEffect(() => {
    const el = bodyRef.current;
    if (el && stickRef.current && !session.stopped) el.scrollTop = el.scrollHeight;
  });
  const onScroll = (e) => {
    const el = e.currentTarget;
    stickRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 8;
  };

  const stopped = !!session.stopped;
  const endTime = stopped ? session.stoppedAt : Date.now();
  const elapsedSec = Math.max(0.001, (endTime - session.started) / 1000);
  const overallRate = session.total / elapsedSec;
  const visible = session.ring;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, fontFamily: 'var(--app-mono)', fontSize: 12 }}>
      <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--app-border)', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--app-fg2)' }}>
        <I.Eye size={13} style={{ color: stopped ? 'var(--app-fg3)' : 'var(--cyan)' }} />
        <b style={{ color: stopped ? 'var(--app-fg2)' : 'var(--cyan)' }}>{stopped ? 'Stopped' : 'Watching'} {session.table}</b>
        <span style={{ color: 'var(--app-fg3)' }}>{session.opts.verbose ? 'VERBOSE' : 'count-only'}{session.opts.fast ? ' · FAST' : ''}{session.opts.where ? ` · WHERE ${session.opts.where}` : ''}</span>
        <span style={{ marginLeft: 'auto', color: 'var(--app-fg2)' }}>
          <b style={{ color: 'var(--app-fg1)' }}>{session.total.toLocaleString()}</b> events · <b style={{ color: 'var(--app-fg1)' }}>{Math.round(overallRate).toLocaleString()}</b>/s {stopped ? 'over run' : 'avg'}
          {session.dropped > 0 && <span style={{ color: 'var(--orange)' }}> · {session.dropped.toLocaleString()} older off-screen</span>}
        </span>
        {stopped
          ? <button className="btn btn-sm btn-ghost" onClick={onClear} title="Discard captured events"><I.Close size={11} /> Clear</button>
          : <button className="btn btn-sm btn-ghost" onClick={onStop}  title={`Pause: UNWATCH ${session.table} but keep what's in the buffer`}><I.Close size={11} /> Stop</button>}
      </div>
      <div ref={bodyRef} onScroll={onScroll} style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '6px 16px', lineHeight: 1.45 }}>
        {visible.length === 0 ? (
          <div style={{ color: 'var(--app-fg3)' }}>{stopped ? 'No events were captured.' : <>Waiting for changes on <span style={{ color: 'var(--cyan)' }}>{session.table}</span>…</>}</div>
        ) : visible.map((e, i) => <WatchEventLine key={i} ev={e} />)}
      </div>
    </div>
  );
}

function WatchEventLine({ ev }) {
  // Lines come pre-classified and pre-stringified by the watch callback,
  // so render is just a timestamp + the precomputed preview. Stringify
  // happens on the data-path (rAF-throttled), keeping render trivial.
  const ts = new Date(ev.t);
  const stamp = `${ts.toTimeString().slice(0,8)}.${String(ts.getMilliseconds()).padStart(3,'0')}`;
  const color = ev.kind === 'count' ? 'var(--cyan)' : 'var(--green)';
  return (
    <div style={{ display: 'flex', gap: 10, whiteSpace: 'pre' }}>
      <span style={{ color: 'var(--app-fg4)' }}>{stamp}</span>
      <span style={{ color }}>{ev.preview}</span>
    </div>
  );
}

// Render a stack of result blocks for multi-statement runs. Each block shows
// the SQL it ran (truncated) + a status pill + the result table inline.
function MultiResults({ items, view }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12, overflow: 'auto', height: '100%' }}>
      {items.map((it, i) => {
        const isErr = !!it.error || it.result?.type === 'error';
        return (
          <div key={i} style={{ border: '1px solid var(--app-border)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--app-surface-2)', borderBottom: '1px solid var(--app-border)', fontSize: 12 }}>
              <span style={{ color: 'var(--app-fg3)', fontFamily: 'var(--app-mono)', minWidth: 24 }}>{i + 1}.</span>
              <span className="mono" style={{ flex: 1, color: 'var(--app-fg2)', fontFamily: 'var(--app-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.sql}>{it.sql}</span>
              {!isErr && it.result?.type === 'rows' && (
                <span className="pill green" style={{ fontSize: 10 }}>{it.result.count} row{it.result.count === 1 ? '' : 's'}</span>
              )}
              {!isErr && it.result?.type !== 'rows' && it.result?.type !== 'error' && (
                <span className="pill green" style={{ fontSize: 10 }}>ok</span>
              )}
              {isErr && <span className="pill orange" style={{ fontSize: 10 }}>error</span>}
              <span style={{ color: 'var(--app-fg4)', fontFamily: 'var(--app-mono)', fontSize: 11 }}>{it.ms} ms</span>
            </div>
            <div style={{ minHeight: 60, maxHeight: 360, overflow: 'auto' }}>
              {it.error
                ? <ErrorMessage message={it.error} />
                : <ResultsTable result={it.result} view={view} />}
            </div>
          </div>
        );
      })}
      {items.length > 0 && items[items.length - 1].error == null && items[items.length - 1].result?.type !== 'error' && (
        <div style={{ color: 'var(--app-fg3)', fontSize: 11, textAlign: 'center' }}>
          ran {items.length} statements
        </div>
      )}
    </div>
  );
}

function ResultsTable({ result, view }) {
  if (!result) {
    return <div style={{ padding: 32, color: 'var(--app-fg3)', textAlign: 'center', fontSize: 13 }}>Run a query to see results.</div>;
  }
  if (result.type === 'multi')                 return <MultiResults items={result.items} view={view} />;
  if (result.type === 'error')                 return <ErrorMessage message={result.message} />;
  if (result.type !== 'rows')                   return <StatusMessage result={result} />;

  const { columns, data, count } = result;
  // B607: `result.types` is the column header the SERVER sent with the rows —
  // authoritative, aligned by position, and free. It beats `colTypes`, which is
  // a best-effort `SHOW COLUMNS FROM <table>` lookup that only resolves when the
  // SQL names one source table and the result column names match its schema.
  const colTypes = wireColTypes(result) || result.colTypes || null;

  if (view === 'json') {
    const obj = data.map(row => Object.fromEntries(columns.map((c, i) => [c, row[i]])));
    return (
      <pre style={{ margin: 0, padding: 16, fontFamily: 'var(--app-mono)', fontSize: 12, color: 'var(--app-fg1)', overflow: 'auto', height: '100%' }}>
{JSON.stringify(obj, jsonSafeReplacer, 2)}
      </pre>
    );
  }
  if (view === 'plan') {
    if (!isPlanResult(result)) {
      return (
        <div style={{ padding: 24, color: 'var(--app-fg3)', fontSize: 13, lineHeight: 1.6 }}>
          The current result isn't a query plan. Click <b style={{ color: 'var(--app-fg1)' }}>Run</b> with this tab active and the query will be sent as <span className="mono" style={{ color: 'var(--cyan)' }}>EXPLAIN …</span>.
        </div>
      );
    }
    return (
      <div style={{ padding: 16, fontFamily: 'var(--app-mono)', fontSize: 13, lineHeight: 1.6, overflow: 'auto', height: '100%' }}>
        {data.map((row, i) => {
          const [step, op, detail] = row;
          const opColor = OP_COLOR[op] || 'var(--cyan)';
          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '32px 200px 1fr', alignItems: 'baseline', gap: 12, padding: '6px 4px', borderBottom: i < data.length - 1 ? '1px solid var(--app-border)' : 0 }}>
              <span style={{ color: 'var(--app-fg4)', textAlign: 'right' }}>{step}</span>
              <span style={{ color: opColor, fontWeight: 600 }}>{op}</span>
              <span style={{ color: 'var(--app-fg2)' }}>{detail}</span>
            </div>
          );
        })}
      </div>
    );
  }
  if (view === 'chart') {
    return <ChartView result={result} />;
  }

  // table view
  if (count === 0) {
    return <div style={{ padding: 32, color: 'var(--app-fg3)', textAlign: 'center', fontSize: 13 }}>0 rows.</div>;
  }
  // A DOCUMENT collection always arrives as `_id, document` with the JSON as
  // one text blob, which makes a projection impossible to read at a glance.
  // Expand its top-level keys into columns HERE, in the table view only — the
  // JSON tab above still shows the raw document, so nothing is hidden and the
  // wire shape is unchanged. Ordinary table/ledger results pass through
  // untouched (same object identity).
  const flat = flattenDocumentColumn(result);
  const showCols = flat.columns;
  const showData = flat.data;
  // Column types were resolved against the ORIGINAL columns; once flattened
  // they no longer line up, so drop them rather than mislabel a cell (a wrong
  // Timestamp coercion would render a number as a bogus date).
  const showTypes = flat === result ? colTypes : null;
  return (
    <table className="r-table">
      <thead>
        <tr>
          <th className="row-num">#</th>
          {showCols.map(c => <th key={c}>{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {showData.map((row, i) => (
          <tr key={i}>
            <td className="row-num">{i + 1}</td>
            {row.map((v, j) => {
              const t = showTypes?.[j];
              const num = (typeof v === 'number' || typeof v === 'bigint') && t !== 'Timestamp';
              return <td key={j} className={num ? 'num' : ''}>{renderCell(v, t)}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Format a kinedb `TIMESTAMP` cell (i64 MICROSECONDS since epoch, UTC — the raw
// instant the server sends untouched) into a readable BROWSER-LOCAL date. All
// timezone handling happens here, in the client, never on the server.
function fmtTs(us) {
  const ms = Number(us) / 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(us);
  const p = (n, l = 2) => String(n).padStart(l, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
    + `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}

// Per-table column-type cache, filled lazily from `SHOW COLUMNS FROM <table>`.
// Cleared on DDL (see `runForTab`). Module-level so it survives re-renders.
// B1691: each entry belongs to the database and branch it was read in.
const _colTypeCache = createColTypeCache({
  sql: wsSql,
  scope: () => `${getCurrentDb()}/${getCurrentBranch()}`,
});

// Best-effort per-result column types, aligned to `columns`. Resolves the FROM
// table and matches result column names against its schema; unmatched columns
// (aliases, joins, expressions) get `null` and render as today. Returns `null`
// when no single source table is identifiable.
const colTypesForResult = _colTypeCache.colTypesForResult;

function renderCell(v, type) {
  if (v === null || v === undefined) return <span className="null">null</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (type === 'Timestamp' && (typeof v === 'number' || typeof v === 'bigint')) return fmtTs(v);
  // B607: an INT past 2^53 is a BigInt. It is a number to the reader, so it
  // renders bare — no monospace cell, no quotes, every digit intact.
  if (typeof v === 'bigint') return v.toString();
  // The remaining typed shapes are binary: `Bytes` as `\x…` hex, `Vector` as a
  // bracketed list, `Blob` as its chunk count. They read best in the same
  // monospace cell a JSON value gets.
  const typed = formatTypedCell(v);
  if (typed !== null) return <span className="json-cell">{typed}</span>;
  if (typeof v === 'object')  return <span className="json-cell">{JSON.stringify(v, jsonSafeReplacer)}</span>;
  return String(v);
}

// ── Main component ────────────────────────────────────────────────────

export function QueryConsole() {
  const [tabs, setTabs]           = useLocalState('kinedb.qc.tabs', DEFAULT_TABS);
  const [activeTab, setActiveTab] = useLocalState('kinedb.qc.active', 1);
  const [history, setHistory]     = useLocalState('kinedb.qc.history', []);
  const [saved, setSaved]         = useLocalState('kinedb.qc.saved', []);
  // Layout state — splitters between qc-sidebar/main and editor/results.
  const [qcSidebarWidth, setQcSidebarWidth] = useLocalState('kinedb.qc.sidebarWidth', 248);
  const [qcSidebarCollapsed, setQcSidebarCollapsed] = useLocalState('kinedb.qc.sidebarCollapsed', false);
  const [qcEditorRatio, setQcEditorRatio] = useLocalState('kinedb.qc.editorRatio', 0.4);
  const [pane, setPane]           = useState('schema');
  const [view, setView]           = useState('table');
  const [filter, setFilter]       = useState('');
  // Per-tab transient run state, keyed by tab id. Not persisted.
  const [runState, setRunState]   = useState({}); // id → { running, result, error, ms, ranAt }
  // Rename-in-progress state. Double-click a tab name to enter rename mode.
  const [renamingTabId, setRenamingTabId] = useState(null);
  const [renameDraft, setRenameDraft]     = useState('');
  // Drag-to-reorder state. `dragTabId` is the tab being dragged; `dropIdx`
  // is where it would land if released now (insertion-line index, 0..N).
  const [dragTabId, setDragTabId] = useState(null);
  const [dropIdx, setDropIdx]     = useState(null);
  // Bumped whenever the live store changes, forcing this component to
  // re-render so the per-tab "Live" indicator + unseen counts stay current.
  const [, setLiveTick]           = useState(0);
  const reloadKey = useRef(0);                    // bumping forces SchemaTree to re-fetch

  const tab = tabs.find(t => t.id === activeTab) || tabs[0];
  const tabRun = runState[tab?.id] || {};

  // Reset filter when switching panes so it doesn't carry over between views.
  useEffect(() => { setFilter(''); }, [pane]);

  useEffect(() => live.subscribe(() => setLiveTick(x => x + 1)), []);

  // Clear the active tab's unseen count when:
  //   - the user switches to it (focus → "I'm looking at this now")
  //   - its result refreshes (live re-run finished → "I now see the new data")
  // Crucially, NOT on every render — otherwise the count visibly flashes to
  // 0 between the notify and the re-run, and the user never sees the badge.
  useEffect(() => { if (tab) live.markRead(tab.id); }, [tab?.id, tabRun.result]);

  // Editing the query while Live is on auto-disables Live: the active
  // subscription is for the *prior* table/query, so silently re-keying it
  // would surprise users. They re-toggle Live to apply the new query.
  const onChangeQuery = (v) => {
    if (live.isLive(activeTab)) live.disable(activeTab);
    setTabs(tabs.map(t => t.id === activeTab ? { ...t, query: v } : t));
  };

  const onCloseTab = (id, e) => {
    e?.stopPropagation();
    if (live.isLive(id)) live.disable(id);
    if (watchSessionsRef.current.has(id)) stopWatchSession(id);
    const next = tabs.filter(t => t.id !== id);
    if (next.length === 0) {
      const fresh = { id: Date.now(), name: 'Untitled', query: '' };
      setTabs([fresh]); setActiveTab(fresh.id); return;
    }
    setTabs(next);
    if (id === activeTab) setActiveTab(next[0].id);
  };

  const onAddTab = () => {
    const nid = (tabs.length ? Math.max(...tabs.map(t => t.id)) : 0) + 1;
    setTabs([...tabs, { id: nid, name: `Untitled ${nid}`, query: '' }]);
    setActiveTab(nid);
  };

  // Drag/drop handlers. Native HTML5 dnd: dataTransfer carries the tab id
  // (so cross-window drops would be possible later); on drop we splice the
  // tabs array.
  const onTabDragStart = (e, id) => {
    setDragTabId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(id)); } catch { /* ignore */ }
  };
  const onTabDragOver = (e, idx) => {
    if (dragTabId == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Decide whether the drop lands before or after this tab based on x-position.
    const r = e.currentTarget.getBoundingClientRect();
    const before = (e.clientX - r.left) < r.width / 2;
    setDropIdx(before ? idx : idx + 1);
  };
  const onTabDrop = (e) => {
    e.preventDefault();
    if (dragTabId == null || dropIdx == null) { setDragTabId(null); setDropIdx(null); return; }
    const from = tabs.findIndex(t => t.id === dragTabId);
    if (from < 0) { setDragTabId(null); setDropIdx(null); return; }
    const to = dropIdx > from ? dropIdx - 1 : dropIdx;     // account for removed source
    if (to !== from) {
      const next = tabs.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      setTabs(next);
    }
    setDragTabId(null);
    setDropIdx(null);
  };
  const onTabDragEnd = () => { setDragTabId(null); setDropIdx(null); };

  // Splitter drag handlers. Each grabs window-level mousemove/up so the
  // drag continues even if the cursor leaves the small handle area.
  const onSidebarSplitterDown = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = qcSidebarWidth;
    const move = (ev) => setQcSidebarWidth(Math.max(160, Math.min(560, startW + (ev.clientX - startX))));
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  const onEditorSplitterDown = (e) => {
    e.preventDefault();
    const main = e.currentTarget.closest('.qc-main');
    if (!main) return;
    const move = (ev) => {
      const rect = main.getBoundingClientRect();
      const toolbarH = main.firstElementChild?.getBoundingClientRect().height || 0;
      const dividerH = 4;
      const usable = rect.height - toolbarH - dividerH;
      if (usable <= 0) return;
      const editorH = (ev.clientY - rect.top) - toolbarH;
      const ratio = Math.max(0.1, Math.min(0.9, editorH / usable));
      setQcEditorRatio(ratio);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  const startRename = (id, currentName) => {
    setRenamingTabId(id);
    setRenameDraft(currentName ?? '');
  };
  const commitRename = () => {
    const trimmed = renameDraft.trim();
    if (renamingTabId != null && trimmed) {
      setTabs(tabs.map(t => t.id === renamingTabId ? { ...t, name: trimmed } : t));
    }
    setRenamingTabId(null);
  };
  const cancelRename = () => setRenamingTabId(null);

  // Per-tab snapshot of the last result, used by the Live re-run-and-compare
  // path to suppress unseen ticks when a notify didn't actually change this
  // tab's view (e.g. an UPDATE on a row that doesn't match the SELECT's
  // WHERE clause). Lives in a ref because it isn't read during render.
  const lastResultsRef = useRef(new Map());

  // Core run: execute a SQL string for a specific tab, update its run state
  // and history. Keep this independent of `activeTab` / `view` so the live
  // notify callback can drive it for a tab the user isn't even looking at.
  // Returns the new `result` so callers (notably the Live diff loop) can
  // compare against the previous one without re-reading React state.
  const runForTab = useCallback(async (tabId, sql, { recordHistory = true } = {}) => {
    if (!sql) return null;
    const startedAt = Date.now();
    setRunState(s => ({ ...s, [tabId]: { ...(s[tabId] || {}), running: true, ranAt: startedAt } }));
    let result = null, error = null, ms = 0;
    try {
      const r = await wsSql(sql);
      ms = Date.now() - startedAt;
      result = r;
      // Annotate rows with per-column types (from the table's schema) so the
      // table view renders TIMESTAMP columns as local dates. Best-effort: a
      // failed/absent schema lookup just leaves cells as today.
      //
      // B607: skipped entirely when the reply carried its own column header —
      // that header is authoritative and already here, so the extra
      // `SHOW COLUMNS FROM <table>` round trip would only be a slower guess.
      if (result?.type === 'rows' && Array.isArray(result.columns) && !Array.isArray(result.types)) {
        const colTypes = await colTypesForResult(sql, result.columns);
        if (colTypes) result = { ...result, colTypes };
      }
    } catch (e) {
      ms = Date.now() - startedAt;
      if (e instanceof KineDBError && e.response) result = e.response;
      else error = e.message || String(e);
    }
    const serverUs = typeof result?.elapsed_us === 'number' ? result.elapsed_us : null;
    setRunState(s => ({ ...s, [tabId]: { running: false, ranAt: startedAt, ms, serverUs, result, error } }));
    if (recordHistory) {
      const ok = !(error || result?.type === 'error');
      const status = ok ? 'ok' : 'err';
      const count  = result?.type === 'rows' ? result.count
                   : typeof result?.count === 'number' ? result.count
                   : null;
      setHistory(h => [{ ts: Date.now(), sql, ms, serverUs, status, count }, ...h].slice(0, 50));
    }
    if (/\b(create|drop|alter)\b/i.test(sql)) { reloadKey.current++; _colTypeCache.clear(); }
    return result;
  }, [setHistory]);

  // Per-tab streaming watch sessions: not React state because the events
  // arrive at up to 10K/sec — re-rendering on every one would melt the
  // main thread. Events live in a ring buffer here, and the WatchFeed
  // component pulls from it on a rAF-batched tick.
  const watchSessionsRef = useRef(new Map()); // tabId → { table, opts, handle, ring, total, dropped, lastBurst, rateBytes, rateAt, started }

  // Bumped at most once per animation frame when a watched feed has new
  // events to render. Driving the WatchFeed's render off this means even
  // 10K-events/sec only triggers ~60 React renders/sec, and `requestAnimationFrame`
  // throttles further when the tab is hidden.
  const [watchTick, setWatchTick] = useState(0);
  const watchTickPendingRef = useRef(false);
  const scheduleWatchUpdate = useCallback(() => {
    if (watchTickPendingRef.current) return;
    watchTickPendingRef.current = true;
    requestAnimationFrame(() => {
      watchTickPendingRef.current = false;
      setWatchTick(x => x + 1);
    });
  }, []);

  // Stop is a pause, not a wipe — when the user clicks Stop it's because
  // they want to scroll back through the rows that just flew by. The WS
  // subscription is canceled (server stops sending) but the session +
  // ring buffer stay so the feed keeps rendering. `preserve: false` is
  // for the actual teardown paths (tab close, starting a fresh watch on
  // the same tab) where we genuinely want a clean slate.
  const stopWatchSession = useCallback(async (tabId, { preserve = false } = {}) => {
    const sess = watchSessionsRef.current.get(tabId);
    if (!sess) return;
    if (sess.handle) {
      try { await sess.handle.cancel(); } catch { /* socket may be down */ }
      sess.handle = null;
    }
    if (preserve) {
      sess.stopped = true;
      sess.stoppedAt = Date.now();
      scheduleWatchUpdate();
    } else {
      watchSessionsRef.current.delete(tabId);
    }
  }, [scheduleWatchUpdate]);

  // Fully discard a paused session (clears the ring + drops the entry).
  // Bound to the WatchFeed's Clear button.
  const clearWatchSession = useCallback((tabId) => {
    watchSessionsRef.current.delete(tabId);
    setRunState(s => ({ ...s, [tabId]: { ...(s[tabId] || {}), result: null } }));
    scheduleWatchUpdate();
  }, [scheduleWatchUpdate]);

  // Build the per-frame ingestion callback for a given session. Shared by
  // startWatchSession and resumeWatchSession so both paths feed the ring
  // identically — the only difference between fresh start and resume is
  // whether we wipe the buffer first.
  const buildOnFrame = useCallback((sess) => (msg) => {
    const t = Date.now();
    if (msg.type === 'watch_data' && Array.isArray(msg.data) && msg.data.length > 0) {
      const cols = msg.columns || [];
      // Per-frame cap: a single watch_data can carry thousands of rows
      // during seeding. Push only what fits; ring eviction below trims
      // the rest in bulk.
      const take = Math.min(msg.data.length, WATCH_RING_CAPACITY);
      for (let i = 0; i < take; i++) {
        const row = msg.data[i];
        const obj = Object.fromEntries(cols.map((c, j) => [c, row[j]]));
        let preview = JSON.stringify(obj, jsonSafeReplacer);
        if (preview.length > 240) preview = preview.slice(0, 237) + '…';
        sess.ring.push({ t, kind: 'row', preview });
      }
      sess.total += take;
      if (msg.data.length > take) sess.dropped += msg.data.length - take;
    } else {
      const changed = typeof msg.changed === 'number' ? msg.changed : 1;
      sess.ring.push({ t, kind: 'count', preview: `+${changed} change${changed === 1 ? '' : 's'} (count-only)` });
      sess.total += 1;
    }
    // Bulk trim if we overflowed the ring (cheaper than repeated shift()).
    if (sess.ring.length > WATCH_RING_CAPACITY) {
      const drop = sess.ring.length - WATCH_RING_CAPACITY;
      sess.ring.splice(0, drop);
      sess.dropped += drop;
    }
    scheduleWatchUpdate();
  }, [scheduleWatchUpdate]);

  const startWatchSession = useCallback(async (tabId, parsed) => {
    await stopWatchSession(tabId);
    const sock = await getSocket();
    const sess = {
      table: parsed.table,
      opts: parsed.opts,
      ring: [],     // each entry is a single rendered line: { t, kind, preview }
      total: 0,     // total lines fed into the ring (not just visible — ring cap drops oldest)
      dropped: 0,   // lines dropped by ring eviction or per-frame cap
      started: Date.now(),
      handle: null,
    };
    watchSessionsRef.current.set(tabId, sess);
    sess.handle = await sock.watch(parsed.table, buildOnFrame(sess), parsed.opts);
    // Show the panel as a "watching" status so the rest of the chrome (count,
    // elapsed) stays consistent with manual queries.
    setRunState(s => ({ ...s, [tabId]: {
      ...(s[tabId] || {}), running: false, result: { type: 'watching', table: parsed.table }, error: null, ms: 0,
    } }));
  }, [buildOnFrame, stopWatchSession]);

  // Resume a paused session: re-attach a new server subscription on the
  // same ring buffer + counters. Anything captured before the pause stays
  // visible; new frames just append. Used when the user re-clicks the
  // schema's eye button (or types WATCH again) on a tab they previously
  // paused — they get history-plus-live, not a wipe.
  const resumeWatchSession = useCallback(async (tabId) => {
    const sess = watchSessionsRef.current.get(tabId);
    if (!sess || !sess.stopped) return;
    const sock = await getSocket();
    sess.stopped = false;
    sess.stoppedAt = undefined;
    sess.handle = await sock.watch(sess.table, buildOnFrame(sess), sess.opts);
    scheduleWatchUpdate();
  }, [buildOnFrame, scheduleWatchUpdate]);

  // The Run button: respects the current view (Plan → EXPLAIN), records to
  // history. Live mode re-runs use the raw SELECT and skip history.
  const onRun = useCallback(async () => {
    if (!tab || !tab.query.trim() || tabRun.running) return;
    const baseSql = normalizeSql(tab.query);
    if (!baseSql) return;

    // WATCH foo [VERBOSE] [FAST] [WHERE ...] → start a streaming feed. This
    // is the change-stream tail, distinct from the Live toggle (which
    // re-runs a SELECT on each notify). It needs its own data path because
    // a busy table can fire 10K events/sec — way too fast for React state.
    const parsedWatch = parseEditorWatch(baseSql);
    if (parsedWatch) {
      try {
        const sess = watchSessionsRef.current.get(tab.id);
        // If the same query is already paused on this tab, resume — keeps
        // the captured ring intact. Otherwise (different table, different
        // opts, or no session) start fresh.
        if (sess && sess.stopped && sess.table === parsedWatch.table) {
          await resumeWatchSession(tab.id);
        } else {
          await startWatchSession(tab.id, parsedWatch);
        }
      } catch (e) {
        setRunState(s => ({ ...s, [tab.id]: { ...(s[tab.id] || {}), running: false, error: e.message || String(e) } }));
      }
      return;
    }
    const unwatchTbl = parseEditorUnwatch(baseSql);
    if (unwatchTbl) {
      await stopWatchSession(tab.id);
      try {
        // Send the literal UNWATCH so the server sees the explicit user
        // intent (in case there are subs from other paths).
        const r = await wsSql(`UNWATCH ${unwatchTbl}`);
        setRunState(s => ({ ...s, [tab.id]: { running: false, ranAt: Date.now(), ms: 0, serverUs: r?.elapsed_us ?? null, result: r, error: null } }));
      } catch (e) {
        setRunState(s => ({ ...s, [tab.id]: { ...(s[tab.id] || {}), running: false, error: e.message || String(e) } }));
      }
      return;
    }

    // Multi-statement: split on `;` (string-aware), run sequentially, and
    // accumulate one result block per statement. Halts on first failure.
    // B2174: a BEGIN … COMMIT block is ONE transaction() call, a BEGIN …
    // ROLLBACK block is not sent, and an error of the block form sends
    // nothing (`../data/txn-blocks.js`).
    const stmts = splitSql(baseSql);
    if (stmts.length > 1 || (stmts.length === 1 && /^(BEGIN|COMMIT|ROLLBACK)\b/i.test(stmts[0]))) {
      const startedAt = Date.now();
      let units;
      try { units = planBlocks(stmts); }
      catch (e) {
        setRunState(s => ({ ...s, [tab.id]: { ...(s[tab.id] || {}), running: false, ranAt: startedAt, error: e.message } }));
        return;
      }
      setRunState(s => ({ ...s, [tab.id]: { ...(s[tab.id] || {}), running: true, ranAt: startedAt } }));
      const items = [];
      for (let i = 0; i < units.length; i++) {
        const unit = units[i];
        const one = unit.kind === 'stmt' ? unit.sql
          : unit.kind === 'txn' ? `BEGIN … COMMIT (${unit.stmts.length} statement(s), one transaction)`
          : `BEGIN … ROLLBACK (${unit.count} statement(s), not sent)`;
        const t0 = Date.now();
        let r = null, err = null;
        try {
          if (unit.kind === 'stmt') r = await wsSql(unit.sql);
          else if (unit.kind === 'txn') r = await wsTransaction(unit.stmts, unit.token === null ? {} : { token: unit.token });
          else r = { type: 'created' };
        }
        catch (e) {
          if (e instanceof KineDBError && e.response) r = e.response;
          else err = e.message || String(e);
        }
        const ms = Date.now() - t0;
        const serverUs = typeof r?.elapsed_us === 'number' ? r.elapsed_us : null;
        items.push({ sql: one, result: r, error: err, ms, serverUs });
        const ok = !(err || r?.type === 'error');
        setHistory(h => [{ ts: Date.now(), sql: one, ms, serverUs, status: ok ? 'ok' : 'err',
                            count: r?.type === 'rows' ? r.count : (typeof r?.count === 'number' ? r.count : null) }, ...h].slice(0, 50));
        if (!ok) break;     // halt the chain on first error
        if (/\b(create|drop|alter)\b/i.test(one)) reloadKey.current++;
      }
      const totalMs = Date.now() - startedAt;
      setRunState(s => ({ ...s, [tab.id]: { running: false, ranAt: startedAt, ms: totalMs, serverUs: null,
                                             result: { type: 'multi', items }, error: null } }));
      return;
    }

    const sql = view === 'plan' ? withExplain(baseSql) : baseSql;
    await runForTab(tab.id, sql);
  }, [tab, tabRun.running, view, runForTab, startWatchSession, resumeWatchSession, stopWatchSession, setHistory]);

  // Toggle Live for the active tab. Disabled by the toolbar when the query
  // isn't watchable; the parser's reason is shown as a tooltip instead.
  const onToggleLive = useCallback(async () => {
    if (!tab) return;
    const tabId = tab.id;
    if (live.isLive(tabId)) {
      await live.disable(tabId);
      lastResultsRef.current.delete(tabId);
      return;
    }
    const baseSql = normalizeSql(tab.query);
    const parsed = parseWatchable(baseSql);
    if (!parsed.ok) return;
    try {
      // The onChange callback re-runs the SELECT and reports back whether
      // this tab's view actually changed. Returning `false` tells the live
      // store to skip the unseen tick — server-side WATCH WHERE doesn't
      // filter UPDATE/DELETE, so without this every irrelevant update on
      // the table would still bump the badge.
      await live.enable(tabId, baseSql, parsed.table, async () => {
        const newResult = await runForTab(tabId, baseSql, { recordHistory: false });
        const prev = lastResultsRef.current.get(tabId);
        lastResultsRef.current.set(tabId, newResult);
        return !resultsEqual(prev, newResult);
      });
      // Baseline run — establish the "last result" so the very first notify
      // has something to diff against. Don't tick unseen for this run.
      const baseline = await runForTab(tabId, baseSql, { recordHistory: false });
      lastResultsRef.current.set(tabId, baseline);
    } catch (e) {
      setRunState(s => ({ ...s, [tabId]: { ...(s[tabId] || {}), error: `Couldn\'t start Live mode: ${e.message || e}` } }));
    }
  }, [tab, runForTab]);

  const onOpenAsTab = (name, query) => {
    const nid = (tabs.length ? Math.max(...tabs.map(t => t.id)) : 0) + 1;
    const next = [...tabs, { id: nid, name, query }];
    setTabs(next);
    setActiveTab(nid);
  };

  // Eye button in the schema tree: open or focus a "Watch <table>" tab and
  // start streaming. Reuses an existing tab whose query is the same WATCH
  // statement instead of piling up duplicates.
  const onWatchTable = useCallback(async (name) => {
    const watchSql = `WATCH ${name} VERBOSE`;
    const existing = tabs.find(t => t.query === watchSql);
    let tabId;
    if (existing) {
      tabId = existing.id;
      setActiveTab(tabId);
    } else {
      tabId = (tabs.length ? Math.max(...tabs.map(t => t.id)) : 0) + 1;
      setTabs([...tabs, { id: tabId, name: `Watch ${name}`, query: watchSql }]);
      setActiveTab(tabId);
    }
    const sess = watchSessionsRef.current.get(tabId);
    try {
      if (!sess) {
        await startWatchSession(tabId, { table: name, opts: { verbose: true } });
      } else if (sess.stopped) {
        await resumeWatchSession(tabId);
      } // else: already running, nothing to do
    } catch (e) {
      setRunState(s => ({ ...s, [tabId]: { ...(s[tabId] || {}), running: false, error: e.message || String(e) } }));
    }
  }, [tabs, setTabs, setActiveTab, startWatchSession, resumeWatchSession]);

  const onSave = () => {
    if (!tab || !tab.query.trim()) return;
    const name = window.prompt('Save query as:', tab.name);
    if (!name) return;
    setSaved(s => [{ name, sql: tab.query, savedAt: Date.now() }, ...s.filter(x => x.name !== name)]);
  };

  const onExport = () => {
    if (!tabRun.result || tabRun.result.type !== 'rows') return;
    const { columns, data } = tabRun.result;
    const obj = data.map(row => Object.fromEntries(columns.map((c, i) => [c, row[i]])));
    const blob = new Blob([JSON.stringify(obj, jsonSafeReplacer, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${tab.name.replace(/\W+/g, '_')}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const loadIntoActive = (sql, name) => {
    setTabs(tabs.map(t => t.id === activeTab ? { ...t, query: sql, ...(name ? { name } : {}) } : t));
  };

  // Watchability check for the active tab — drives the Live toolbar button.
  const watchable = tab ? parseWatchable(normalizeSql(tab.query || '')) : { ok: false, reason: '' };
  const tabLive = tab ? live.isLive(tab.id) : false;

  return (
    <div className="page">
      <div className="page-tabs" onDrop={onTabDrop} onDragOver={e => e.preventDefault()}>
        {tabs.map((t, idx) => {
          const tLive = live.isLive(t.id);
          const tUnseen = live.getUnseen(t.id);
          const showLeftLine  = dragTabId != null && dropIdx === idx;
          const showRightLine = dragTabId != null && dropIdx === idx + 1;
          return (
            <div
              key={t.id}
              className={'tab' + (t.id === activeTab ? ' active' : '') + (t.id === dragTabId ? ' dragging' : '') + (showLeftLine ? ' drop-left' : '') + (showRightLine ? ' drop-right' : '')}
              draggable={renamingTabId !== t.id}
              onDragStart={e => onTabDragStart(e, t.id)}
              onDragOver={e => onTabDragOver(e, idx)}
              onDragEnd={onTabDragEnd}
              onClick={() => setActiveTab(t.id)}>
              <I.Query size={13} />
              {renamingTabId === t.id ? (
                <input
                  className="tab-rename-input"
                  value={renameDraft}
                  autoFocus
                  onChange={e => setRenameDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter')       { e.preventDefault(); commitRename(); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                  }}
                  onBlur={commitRename}
                  onClick={e => e.stopPropagation()}
                  onDoubleClick={e => e.stopPropagation()}
                  onFocus={e => e.target.select()}
                />
              ) : (
                <span
                  onDoubleClick={e => { e.stopPropagation(); startRename(t.id, t.name); }}
                  title="double-click to rename"
                >{t.name}</span>
              )}
              {tLive && (tUnseen > 0
                ? <span className="tab-live-count" title={`${tUnseen} unseen change${tUnseen === 1 ? '' : 's'}`}>{tUnseen}</span>
                : <span className="tab-live-dot" title="Live · no unseen changes" />
              )}
              <span className="close" onClick={e => onCloseTab(t.id, e)}><I.Close size={10} /></span>
            </div>
          );
        })}
        <div className="tab add" onClick={onAddTab}><I.Plus size={13} /></div>
      </div>
      <div className="page-body">
        <div className="qc" style={{ gridTemplateColumns: `${qcSidebarCollapsed ? 32 : qcSidebarWidth}px 1fr` }}>
          <div className={'qc-sidebar' + (qcSidebarCollapsed ? ' collapsed' : '')}>
            {qcSidebarCollapsed ? (
              <button
                type="button"
                className="qc-sidebar-expand"
                onClick={() => setQcSidebarCollapsed(false)}
                title="Show schema / history / saved"
              ><I.ChevRight size={14} /></button>
            ) : (
              <>
                <div className="qc-sidebar-head">
                  <div className="search">
                    <I.Search size={13} />
                    <input
                      value={filter}
                      onChange={e => setFilter(e.target.value)}
                      placeholder={pane === 'schema' ? 'Filter tables…' : pane === 'history' ? 'Search history…' : 'Search saved…'}
                    />
                  </div>
                  <button
                    type="button"
                    className="qc-sidebar-collapse"
                    onClick={() => setQcSidebarCollapsed(true)}
                    title="Hide sidebar"
                  ><I.ChevRight size={14} style={{ transform: 'rotate(180deg)' }} /></button>
                </div>
                <div className="pane-tabs">
                  {[['schema', 'Schema'], ['history', 'History'], ['saved', 'Saved']].map(([k, l]) => (
                    <div key={k} className={'pane-tab' + (pane === k ? ' active' : '')} onClick={() => setPane(k)}>{l}</div>
                  ))}
                </div>
                {pane === 'schema'  && <SchemaTree
                  key={reloadKey.current}
                  filter={filter}
                  onOpenAsTab={onOpenAsTab}
                  onWatchTable={onWatchTable}
                />}
                {pane === 'history' && <HistoryList items={history} filter={filter} onPick={h => loadIntoActive(h.sql)} />}
                {pane === 'saved'   && <SavedList items={saved} filter={filter}
                  onPick={s => loadIntoActive(s.sql, s.name)}
                  onDelete={i => setSaved(s => s.filter((_, j) => j !== i))} />}
                <div className="qc-sidebar-handle" onMouseDown={onSidebarSplitterDown} title="Drag to resize" />
              </>
            )}
          </div>
          <div className="qc-main" style={{ gridTemplateRows: `auto ${qcEditorRatio * 100}fr auto ${(1 - qcEditorRatio) * 100}fr` }}>
            <div className="qc-toolbar">
              <button className="btn btn-primary btn-sm" onClick={onRun} disabled={tabRun.running || !tab?.query.trim()}>
                {tabRun.running
                  ? <><I.Refresh size={13} style={{ animation: 'spin 1s linear infinite' }} /> Running…</>
                  : <><I.Play size={11} /> Run{view === 'plan' ? ' · EXPLAIN' : ''}</>}
                <span style={{ marginLeft: 8, padding: '1px 5px', background: 'rgba(4,32,41,0.3)', borderRadius: 3, fontSize: 10, fontFamily: 'var(--app-mono)' }}>⌘↵</span>
              </button>
              <button
                className={'btn btn-sm ' + (tabLive ? 'btn-primary' : 'btn-ghost')}
                onClick={onToggleLive}
                disabled={!tabLive && !watchable.ok}
                title={tabLive
                  ? 'Stop watching this table'
                  : watchable.ok
                    ? `Re-run automatically when ${watchable.table} changes`
                    : watchable.reason}
                style={tabLive ? { background: 'rgba(0,230,118,0.16)', color: 'var(--green)', borderColor: 'rgba(0,230,118,0.3)' } : undefined}>
                <I.Eye size={13} />
                <span>{tabLive ? 'Live · ON' : 'Live'}</span>
              </button>
              <div className="seg">
                <button className="active"><I.Sparkles size={11} style={{ color: 'var(--cyan)' }} /> SQL</button>
              </div>
              <div className="right">
                <button className="btn btn-sm btn-ghost" onClick={onSave} disabled={!tab?.query.trim()}><I.Save size={13} /> Save</button>
                <button className="btn btn-sm btn-ghost" onClick={onExport} disabled={tabRun.result?.type !== 'rows'}><I.Download size={13} /> Export</button>
                <button className="btn btn-sm btn-ghost" onClick={() => { reloadKey.current++; setRunState(s => ({ ...s })); }} title="Refresh schema"><I.Refresh size={13} /></button>
              </div>
            </div>
            <QueryEditor value={tab?.query || ''} onChange={onChangeQuery} onRun={onRun} />
            <div className="qc-divider" onMouseDown={onEditorSplitterDown} title="Drag to resize" />
            <div className="qc-results">
              <div className="qc-results-head">
                <div className="seg">
                  {[['table', 'Table'], ['json', 'JSON'], ['chart', 'Chart'], ['plan', 'Plan']].map(([k, l]) => (
                    <button key={k} className={view === k ? 'active' : ''} onClick={() => setView(k)}>{l}</button>
                  ))}
                </div>
                <div className="stats">
                  {tabRun.result?.type === 'rows'
                    ? <>
                        <span className="s"><b>{tabRun.result.count}</b> row{tabRun.result.count === 1 ? '' : 's'}</span>
                        <ElapsedStats clientMs={tabRun.ms} serverUs={tabRun.serverUs} />
                      </>
                    : tabRun.result?.type === 'multi'
                      ? <>
                          <span className="s"><b>{tabRun.result.items.length}</b> statement{tabRun.result.items.length === 1 ? '' : 's'}</span>
                          <ElapsedStats clientMs={tabRun.ms} serverUs={null} />
                        </>
                      : tabRun.error || tabRun.result?.type === 'error'
                        ? <span className="s" style={{ color: 'var(--orange)' }}>error</span>
                        : tabRun.result
                          ? <ElapsedStats clientMs={tabRun.ms} serverUs={tabRun.serverUs} />
                          : <span className="s" style={{ color: 'var(--app-fg3)' }}>idle</span>}
                </div>
              </div>
              <div className="qc-results-body">
                {(() => {
                  const sess = tab ? watchSessionsRef.current.get(tab.id) : null;
                  if (sess) return <WatchFeed
                    session={sess}
                    watchTick={watchTick}
                    onStop={() => stopWatchSession(tab.id, { preserve: true })}
                    onClear={() => clearWatchSession(tab.id)}
                  />;
                  if (tabRun.error) return <ErrorMessage message={tabRun.error} />;
                  return <ResultsTable result={tabRun.result} view={view} />;
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
